import { useEffect, useState } from 'react';

export type CapabilityState = 'ok' | 'miss' | 'bad' | 'unknown';

export interface Capabilities {
  notifications: CapabilityState;
  serviceWorker: CapabilityState;
  webPush: CapabilityState;
  wakeLock: CapabilityState;
  pwaInstalled: CapabilityState;
  audioHeartbeat: CapabilityState;
}

export type OverallReach = 'green' | 'yellow' | 'red';

const initialCaps: Capabilities = {
  notifications: 'unknown',
  serviceWorker: 'unknown',
  webPush: 'unknown',
  wakeLock: 'unknown',
  pwaInstalled: 'unknown',
  audioHeartbeat: 'ok', // immer aktivierbar als Backup
};

function overallFrom(c: Capabilities): OverallReach {
  if (c.notifications === 'ok' && c.serviceWorker === 'ok' && c.webPush === 'ok') return 'green';
  if (c.notifications === 'bad' || c.serviceWorker === 'bad') return 'red';
  return 'yellow';
}

export function useReachability() {
  const [caps, setCaps] = useState<Capabilities>(initialCaps);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      const next: Capabilities = { ...initialCaps };

      // Notification permission
      if ('Notification' in window) {
        const p = Notification.permission;
        next.notifications = p === 'granted' ? 'ok' : p === 'denied' ? 'bad' : 'miss';
      } else {
        next.notifications = 'bad';
      }

      // Service Worker
      if ('serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.register('/sw.js');
          next.serviceWorker = reg.active ? 'ok' : 'miss';

          // Web Push subscription
          try {
            const sub = await reg.pushManager.getSubscription();
            next.webPush = sub ? 'ok' : 'miss';
          } catch (e) {
            next.webPush = 'bad';
          }
        } catch (e) {
          next.serviceWorker = 'bad';
        }
      } else {
        next.serviceWorker = 'bad';
        next.webPush = 'bad';
      }

      // Wake Lock
      next.wakeLock = 'wakeLock' in navigator ? 'ok' : 'miss';

      // PWA installed (display-mode standalone)
      const standalone =
        window.matchMedia?.('(display-mode: standalone)').matches ||
        (navigator as any).standalone === true;
      next.pwaInstalled = standalone ? 'ok' : 'miss';

      if (!cancelled) setCaps(next);
    }

    detect();

    // Listen for SW messages (subscription change)
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'sw:subscription_change') {
        detect();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);

    return () => {
      cancelled = true;
      navigator.serviceWorker?.removeEventListener('message', handler);
    };
  }, []);

  return { caps, overall: overallFrom(caps) };
}

/** Wake Lock während aktiver Session halten */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    if (!('wakeLock' in navigator)) return;

    let lock: any = null;
    let released = false;

    async function acquire() {
      try {
        lock = await (navigator as any).wakeLock.request('screen');
      } catch (e) {
        // typically: page not visible
      }
    }

    const visHandler = () => {
      if (document.visibilityState === 'visible' && !released && !lock) {
        acquire();
      }
    };

    acquire();
    document.addEventListener('visibilitychange', visHandler);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', visHandler);
      lock?.release?.();
    };
  }, [active]);
}

/** Silent audio heartbeat — hält den Tab als "audible" markiert gegen Discard */
export function useAudioHeartbeat(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    if (!AC) return;

    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0.0001; // praktisch lautlos
    osc.frequency.value = 50;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();

    return () => {
      osc.stop();
      ctx.close();
    };
  }, [active]);
}
